import { CONTRATACAO_PLANOS_PAGOS_DESABILITADA } from '../config/contratacaoPlanos';
import {
	BILLING_PLANS,
	getPlanByCode,
	getPurchasablePlans,
	getStoreProductIds,
} from '../config/billingCatalog';
import { isNativeApp, isWebApp, getAppPlatform } from '../utils/platform';

/**
 * BillingService — Plano B
 *
 * Web: Pagar.me (fluxo existente em cadastro/PaymentDialog)
 * Native: StoreKit / Play Billing (plugin ainda não conectado)
 *
 * Quando o plugin (ex.: RevenueCat) estiver pronto, implementar
 * `purchasePlan` / `restorePurchases` no adapter nativo sem mudar a UI.
 */

/** Defina true após criar produtos nas lojas + configurar o plugin de IAP. */
export const STORE_BILLING_ENABLED = false;

export function mustUseStoreBilling() {
	return isNativeApp();
}

export function canUseWebCardPayment() {
	return isWebApp() && !CONTRATACAO_PLANOS_PAGOS_DESABILITADA;
}

/**
 * Pula etapa de cartão / chamadas payment/cards|subscriptions.
 * - Sempre no app nativo
 * - Na web, quando a flag comercial estiver desligada
 */
export function shouldSkipCardPaymentStep() {
	return mustUseStoreBilling() || !canUseWebCardPayment();
}

export function isStoreBillingConfigured() {
	return STORE_BILLING_ENABLED && getStoreProductIds().length > 0;
}

export function listPlansForCurrentPlatform() {
	if (mustUseStoreBilling()) {
		return BILLING_PLANS.filter(
			(plan) => !plan.purchasable || Boolean(plan.storeProductId)
		);
	}
	return BILLING_PLANS;
}

export function getNativeUpgradeMessage() {
	if (!mustUseStoreBilling()) {
		return null;
	}

	if (!isStoreBillingConfigured()) {
		return {
			title: 'Assinaturas no aplicativo',
			body:
				'Neste app, upgrades de plano serão feitos pela loja (App Store / Google Play), conforme as regras das plataformas. Enquanto a compra in-app não estiver disponível, você pode continuar no plano gratuito ou gerenciar a assinatura pela versão web em mylegis.com.br.',
			ctaLabel: null,
			canPurchase: false,
			canRestore: false,
		};
	}

	return {
		title: 'Assinaturas no aplicativo',
		body:
			'Escolha um plano para assinar pela loja do seu dispositivo. Sua assinatura será sincronizada com sua conta MyLegis.',
		ctaLabel: 'Ver planos',
		canPurchase: true,
		canRestore: true,
	};
}

/**
 * Compra nativa (stub). Conectar RevenueCat / plugin IAP aqui.
 * @param {string} planCode ex.: 'plano-carreira'
 */
export async function purchasePlan(planCode) {
	if (!mustUseStoreBilling()) {
		throw new Error(
			'purchasePlan é exclusivo do app nativo. Na web use o fluxo Pagar.me.'
		);
	}

	const plan = getPlanByCode(planCode);
	if (!plan || !plan.storeProductId) {
		throw new Error(`Plano sem product ID de loja: ${planCode}`);
	}

	if (!isStoreBillingConfigured()) {
		const error = new Error('STORE_BILLING_NOT_CONFIGURED');
		error.code = 'STORE_BILLING_NOT_CONFIGURED';
		error.userMessage =
			'A compra pela loja ainda está em configuração. Tente novamente em breve ou use a versão web.';
		throw error;
	}

	// TODO: integrar @revenuecat/purchases-capacitor (ou plugin IAP)
	// 1. Purchases.purchaseStoreProduct({ productIdentifier: plan.storeProductId })
	// 2. Backend valida recibo / webhook e atualiza users.plano
	throw new Error('STORE_BILLING_ADAPTER_NOT_IMPLEMENTED');
}

/**
 * Restaura compras da loja (stub).
 */
export async function restorePurchases() {
	if (!mustUseStoreBilling()) {
		throw new Error('restorePurchases é exclusivo do app nativo.');
	}

	if (!isStoreBillingConfigured()) {
		const error = new Error('STORE_BILLING_NOT_CONFIGURED');
		error.code = 'STORE_BILLING_NOT_CONFIGURED';
		error.userMessage =
			'A restauração de compras estará disponível após ativarmos as assinaturas na loja.';
		throw error;
	}

	// TODO: Purchases.restorePurchases() + sync entitlement no backend
	throw new Error('STORE_BILLING_ADAPTER_NOT_IMPLEMENTED');
}

export function getBillingDebugInfo() {
	return {
		platform: getAppPlatform(),
		isNative: isNativeApp(),
		mustUseStoreBilling: mustUseStoreBilling(),
		canUseWebCardPayment: canUseWebCardPayment(),
		shouldSkipCardPaymentStep: shouldSkipCardPaymentStep(),
		storeBillingEnabled: STORE_BILLING_ENABLED,
		storeBillingConfigured: isStoreBillingConfigured(),
		purchasablePlans: getPurchasablePlans().map((p) => ({
			code: p.code,
			storeProductId: p.storeProductId,
			pagarmePlanId: p.pagarmePlanId,
		})),
	};
}

export default {
	mustUseStoreBilling,
	canUseWebCardPayment,
	shouldSkipCardPaymentStep,
	isStoreBillingConfigured,
	listPlansForCurrentPlatform,
	getNativeUpgradeMessage,
	purchasePlan,
	restorePurchases,
	getBillingDebugInfo,
};
